import React, { useState } from 'react';
import { PlusIcon, SearchIcon, FilterIcon, EditIcon, TrashIcon, AlertCircleIcon, ToggleLeftIcon, ToggleRightIcon, CheckCircleIcon, XCircleIcon, InfoIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import StatusBadge from '../../components/ui/StatusBadge';
interface Rule {
  id: string;
  name: string;
  description: string;
  ruleType: string;
  threshold: number;
  status: 'active' | 'inactive';
  lastTriggered: string | null;
  createdAt: string;
  createdBy: string;
}
const RulesManagement: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<Rule | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  // Mock data for rules
  const rulesData: Rule[] = [{
    id: 'RULE001',
    name: 'High Transaction Volume',
    description: 'Flag merchants with more than 100 transactions in a day',
    ruleType: 'Transaction Volume',
    threshold: 100,
    status: 'active',
    lastTriggered: '2023-10-28T10:30:00Z',
    createdAt: '2023-09-15T08:00:00Z',
    createdBy: 'admin@example.com'
  }, {
    id: 'RULE002',
    name: 'Large Transaction Amount',
    description: 'Flag transactions over $10,000',
    ruleType: 'Transaction Amount',
    threshold: 10000,
    status: 'active',
    lastTriggered: '2023-10-27T14:45:00Z',
    createdAt: '2023-09-16T09:30:00Z',
    createdBy: 'admin@example.com'
  }, {
    id: 'RULE003',
    name: 'Same Customer Multiple Transactions',
    description: 'Flag if same customer makes more than 5 transactions with same merchant in a day',
    ruleType: 'Customer Pairing',
    threshold: 5,
    status: 'active',
    lastTriggered: '2023-10-26T11:20:00Z',
    createdAt: '2023-09-18T10:15:00Z',
    createdBy: 'riskanalyst@example.com'
  }, {
    id: 'RULE004',
    name: 'Unusual Transaction Pattern',
    description: 'Flag transactions outside normal business hours',
    ruleType: 'Time Pattern',
    threshold: 0,
    status: 'inactive',
    lastTriggered: null,
    createdAt: '2023-09-20T14:00:00Z',
    createdBy: 'admin@example.com'
  }, {
    id: 'RULE005',
    name: 'Multiple Failed Transactions',
    description: 'Flag merchants with more than 3 failed transactions in an hour',
    ruleType: 'Failed Transactions',
    threshold: 3,
    status: 'active',
    lastTriggered: null,
    createdAt: '2023-09-22T16:45:00Z',
    createdBy: 'riskanalyst@example.com'
  }];
  // Filter rules based on search query and status filter
  const filteredRules = rulesData.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase()) || rule.description.toLowerCase().includes(searchQuery.toLowerCase()) || rule.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || rule.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const handleToggleStatus = (rule: Rule) => {
    // In a real app, this would update the rule status in the backend
    console.log(`Toggling status for rule: ${rule.id} to ${rule.status === 'active' ? 'inactive' : 'active'}`);
  };
  const handleDeleteRule = () => {
    if (selectedRule) {
      // In a real app, this would delete the rule in the backend
      console.log(`Deleting rule: ${selectedRule.id}`);
      setShowDeleteModal(false);
      setSelectedRule(null);
    }
  };
  const handleEditRule = (rule: Rule) => {
    // In a real app, this would open the edit form with the rule data
    console.log(`Editing rule: ${rule.id}`);
    setSelectedRule(rule);
    setShowCreateModal(true);
  };
  return <div className="space-y-6" data-id="element-7264">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-7265">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-7266">
          Rules Management
        </h1>
        <div className="mt-3 sm:mt-0" data-id="element-7267">
          <button onClick={() => {
          setSelectedRule(null);
          setShowCreateModal(true);
        }} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-7268">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-7269" />
            Create New Rule
          </button>
        </div>
      </div>
      <Card data-id="element-7270">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-3 md:space-y-0" data-id="element-7271">
          <div className="relative" data-id="element-7272">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-7273">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-7274" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500" placeholder="Search rules..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-7275" />
          </div>
          <div className="flex items-center space-x-4" data-id="element-7276">
            <div className="flex items-center space-x-2" data-id="element-7277">
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7278">
                Status:
              </span>
              <select className="block w-32 pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500" value={statusFilter} onChange={e => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')} data-id="element-7279">
                <option value="all" data-id="element-7280">All</option>
                <option value="active" data-id="element-7281">Active</option>
                <option value="inactive" data-id="element-7282">Inactive</option>
              </select>
            </div>
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7283">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-7284" />
              More Filters
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-7285">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-7286">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-7287">
              <tr data-id="element-7288">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7289">
                  Rule ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7290">
                  Rule Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7291">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7292">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7293">
                  Last Triggered
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7294">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7295">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-7296">
              {filteredRules.map(rule => <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-7297">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-7298">
                    {rule.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-7299">
                    {rule.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" data-id="element-7300">
                    {rule.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7301">
                    {rule.ruleType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7302">
                    {formatDate(rule.lastTriggered)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7303">
                    {rule.status === 'active' ? <StatusBadge status="success" text="Active" data-id="element-7304" /> : <StatusBadge status="default" text="Inactive" data-id="element-7305" />}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7306">
                    <div className="flex items-center justify-end space-x-2" data-id="element-7307">
                      <button onClick={() => handleToggleStatus(rule)} className={`text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300`} title={rule.status === 'active' ? 'Deactivate Rule' : 'Activate Rule'} data-id="element-7308">
                        {rule.status === 'active' ? <ToggleRightIcon className="h-5 w-5 text-green-500" data-id="element-7309" /> : <ToggleLeftIcon className="h-5 w-5" data-id="element-7310" />}
                      </button>
                      <button onClick={() => handleEditRule(rule)} className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" title="Edit Rule" data-id="element-7311">
                        <EditIcon className="h-5 w-5" data-id="element-7312" />
                      </button>
                      <button onClick={() => {
                    setSelectedRule(rule);
                    setShowDeleteModal(true);
                  }} className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300" title="Delete Rule" data-id="element-7313">
                        <TrashIcon className="h-5 w-5" data-id="element-7314" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredRules.length === 0 && <div className="py-8 text-center" data-id="element-7315">
            <AlertCircleIcon className="h-12 w-12 mx-auto text-gray-400" data-id="element-7316" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-7317">
              No rules found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-7318">
              {searchQuery ? "Try adjusting your search or filter to find what you're looking for." : 'Get started by creating a new rule.'}
            </p>
            <div className="mt-6" data-id="element-7319">
              <button onClick={() => {
            setSelectedRule(null);
            setShowCreateModal(true);
          }} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-7320">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-7321" />
                Create New Rule
              </button>
            </div>
          </div>}
      </Card>
      {/* Create/Edit Rule Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title={selectedRule ? 'Edit Rule' : 'Create New Rule'} size="lg" footer={<>
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7323">
              Cancel
            </button>
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-7324">
              {selectedRule ? 'Update Rule' : 'Create Rule'}
            </button>
          </>} data-id="element-7322">
        <div className="space-y-4" data-id="element-7325">
          <div data-id="element-7326">
            <label htmlFor="ruleName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7327">
              Rule Name <span className="text-red-500" data-id="element-7328">*</span>
            </label>
            <input type="text" id="ruleName" className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="Enter rule name" defaultValue={selectedRule?.name || ''} data-id="element-7329" />
          </div>
          <div data-id="element-7330">
            <label htmlFor="ruleDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7331">
              Description <span className="text-red-500" data-id="element-7332">*</span>
            </label>
            <textarea id="ruleDescription" rows={3} className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="Describe the purpose of this rule" defaultValue={selectedRule?.description || ''} data-id="element-7333"></textarea>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7334">
            <div data-id="element-7335">
              <label htmlFor="ruleType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7336">
                Rule Type <span className="text-red-500" data-id="element-7337">*</span>
              </label>
              <select id="ruleType" className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" defaultValue={selectedRule?.ruleType || ''} data-id="element-7338">
                <option value="" data-id="element-7339">Select Rule Type</option>
                <option value="Transaction Volume" data-id="element-7340">Transaction Volume</option>
                <option value="Transaction Amount" data-id="element-7341">Transaction Amount</option>
                <option value="Customer Pairing" data-id="element-7342">Customer Pairing</option>
                <option value="Time Pattern" data-id="element-7343">Time Pattern</option>
                <option value="Failed Transactions" data-id="element-7344">Failed Transactions</option>
                <option value="Geographical" data-id="element-7345">Geographical</option>
              </select>
            </div>
            <div data-id="element-7346">
              <label htmlFor="threshold" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7347">
                Threshold Value
              </label>
              <input type="number" id="threshold" className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="Enter threshold value" defaultValue={selectedRule?.threshold || ''} data-id="element-7348" />
            </div>
          </div>
          <div data-id="element-7349">
            <label htmlFor="actionOnViolation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7350">
              Action on Violation <span className="text-red-500" data-id="element-7351">*</span>
            </label>
            <select id="actionOnViolation" className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-7352">
              <option value="" data-id="element-7353">Select Action</option>
              <option value="flag" data-id="element-7354">Flag Transaction</option>
              <option value="block" data-id="element-7355">Block Transaction</option>
              <option value="notify" data-id="element-7356">Notify Admin</option>
              <option value="flag_notify" data-id="element-7357">Flag & Notify</option>
              <option value="block_notify" data-id="element-7358">Block & Notify</option>
            </select>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-7359">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-7360">
              Rule Conditions
            </h3>
            <div className="space-y-3" data-id="element-7361">
              <div className="flex items-center" data-id="element-7362">
                <input id="condition-same-merchant-customer" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-7363" />
                <label htmlFor="condition-same-merchant-customer" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-7364">
                  Same merchant-customer pair
                </label>
              </div>
              <div className="flex items-center" data-id="element-7365">
                <input id="condition-same-day" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-7366" />
                <label htmlFor="condition-same-day" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-7367">
                  Same day multiple transactions
                </label>
              </div>
              <div className="flex items-center" data-id="element-7368">
                <input id="condition-outside-hours" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-7369" />
                <label htmlFor="condition-outside-hours" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-7370">
                  Outside business hours (8 AM - 8 PM)
                </label>
              </div>
              <div className="flex items-center" data-id="element-7371">
                <input id="condition-cross-border" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-7372" />
                <label htmlFor="condition-cross-border" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-7373">
                  Cross-border transactions
                </label>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-7374">
            <div className="flex items-center justify-between" data-id="element-7375">
              <div className="flex items-center" data-id="element-7376">
                <input id="rule-active" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" defaultChecked={selectedRule ? selectedRule.status === 'active' : true} data-id="element-7377" />
                <label htmlFor="rule-active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-7378">
                  Activate rule immediately
                </label>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md flex items-start" data-id="element-7379">
            <InfoIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-7380" />
            <p className="text-sm text-blue-800 dark:text-blue-300" data-id="element-7381">
              Rules help identify suspicious transaction patterns. Once created,
              they will automatically monitor transactions and generate alerts
              based on the conditions you set.
            </p>
          </div>
        </div>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Rule" footer={<>
            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7383">
              Cancel
            </button>
            <button onClick={handleDeleteRule} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-7384">
              Delete
            </button>
          </>} data-id="element-7382">
        <div className="text-center" data-id="element-7385">
          <AlertCircleIcon className="h-12 w-12 mx-auto text-red-500" data-id="element-7386" />
          <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white" data-id="element-7387">
            Delete Rule
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-7388">
            Are you sure you want to delete the rule "{selectedRule?.name}"?
            This action cannot be undone.
          </p>
        </div>
      </Modal>
    </div>;
};
export default RulesManagement;